// @ts-strict-ignore
import { APP_BASE_HREF, Location, ViewportScroller } from '@angular/common';
import { inject as ngInject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  getIdentityProvider,
  IdentityProvider,
  isLoggedIn,
  type AuthenticationState
} from '@b2x/authentication-library';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { createSelector, Store } from '@ngrx/store';
import type { PublicInformation } from '@owcc/public-connection/dist/connections/owcc-public-data-connection.interface';
import { MediaQueryService, WB_RESIZE_EVENT_NAME, type MediaQuery, type MediaQueryChangeEvent } from '@workbench/core';
import { slashJoin } from 'path-slashes';
import { combineLatest, from, fromEvent, fromEventPattern, merge, of, Subject, type Observable } from 'rxjs';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeWhile,
  tap
} from 'rxjs/operators';

import type { Version } from 'src/app/api/owcc/models';
import { StatusService, VehiclesService } from 'src/app/api/owcc/services';
import { appInject } from 'src/app/app-injector';
import { Mq, MQ6_MIN_WIDTH, MQ7_MIN_WIDTH } from 'src/app/core/config/mq.config';
import { getScriptUrl, URLS } from 'src/app/core/config/url.config';
import {
  OWCC_LOCAL_STORAGE_KEY,
  OWCC_SESSION_STORAGE_KEY,
  SEAMLESS_STORE_SESSION_STORAGE_KEY
} from 'src/app/core/config/web-storage.config';
import { currentHostRef } from 'src/app/core/host-identification/current-host.token';
import { HostId } from 'src/app/core/host-identification/host-id.enum';
import {
  AcknowledgementService,
  type AcknowledgementConfig
} from 'src/app/core/services/acknowledgement/acknowledgement.service';
import { CarlService } from 'src/app/core/services/carl/carl.service';
import { ConsentSystemService } from 'src/app/core/services/consent/consent-system.service';
import {
  ConsoleCommandService,
  type ScreenMirroringCredentials
} from 'src/app/core/services/console-command/console-command.service';
import { DatadogService } from 'src/app/core/services/datadog/datadog.service';
import { FootnotesService } from 'src/app/core/services/footnotes/footnotes.service';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { LinkService } from 'src/app/core/services/link/link.service';
import { SettingsService } from 'src/app/core/services/settings/settings.service';
import { LocalStorageService } from 'src/app/core/services/storage/local-storage/local-storage.service';
import { SessionStorageService } from 'src/app/core/services/storage/session-storage/session-storage.service';
import {
  finishLoadSelectablesAction,
  finishUpdateFeasibleVehicleAction,
  finishUpdateVehicleAction,
  startLoadVehicle
} from 'src/app/features/configuration/store/configuration.actions';
import { startLoadVehiclesAction } from 'src/app/features/motorization/store/motorization.actions';
import { selectApiContext } from 'src/app/select-api-context';
import { ModalService } from 'src/app/shared/modal/modal-service/modal.service';
import type { NavigationItem } from 'src/app/shared/navigation/navigation-item.interface';
import { ScriptLoadingService } from 'src/app/shared/script-loader/script-loading.service';
import { flatten } from 'src/app/utils/functions/flatten';
import { noop } from 'src/app/utils/functions/noop';
import { filterChanged, filterTruthy } from 'src/app/utils/functions/rx-filters';
import { ExecutionMode } from 'src/app/utils/models/execution-mode.enum';
import { Subprocess } from 'src/app/utils/models/subprocess.enum';
import { DURATION_1_SEC } from 'src/app/utils/number-constants';
import { WINDOW } from 'src/app/utils/window.token';

import { b2oSelectors, firstValueFromSync, fromRouter, fromRouterAsSnapshot } from '../router/next-router.selectors';
import { routerNavigationEndAction } from '../router/router.actions';
import { runtimeVariablesUpdate } from '../runtime-variables/runtime-variables.actions';
import {
  selectConfigAndTranslationRelevantParams,
  selectContextVehicleId,
  selectCountry,
  selectOnlineCode,
  selectOnlineCodeRelatedData
} from '../runtime-variables/runtime-variables.selectors';
import { userConfigUpdatePostalCode } from '../user-config/user-config.actions';

import {
  fetchBackendVersion,
  fetchVehicleSummary,
  finishSetNavigationGroups,
  initApp,
  initAppForRuntimeMode,
  invalidateOnlineCode,
  invalidateVehicleSummaryDueToPostalCodeUpdate,
  openRegionPicker,
  setBaseContextForRuntimeMode,
  setIsInAemPreviewMode,
  startSetNavigationGroups,
  udpateBaseContext,
  updateAuthState,
  updateBackendVersion,
  updateConfig,
  updateCurrentMotorization,
  updateMediaQuery,
  updateOnlineCode,
  updateOnlineCodeData,
  updateScreenMirroringCredentials,
  updateTranslations,
  updateVehicleSummary
} from './common.actions';
import {
  fromCarl,
  fromNavigation,
  selectCommonVehicleImageUrl,
  selectCountryAndLanguageByIntegrationMode,
  selectCurrentMotorization,
  selectHasPreconfigs,
  selectHasVehicleLengths,
  selectSelectedVehicleName
} from './common.selectors';
import { seamlessStoreFns, type SeamlessStoreRegionPickerState } from './common.utils';
import { getCustomNavigationGroups } from './navigation.utils';

/**
 * Observes given element with MutationObserver.
 * Beside observable of mutations it returns an API to actively observe or (temporarily) disconnect.
 * Mutations so then just be observed if really required to not have negative performance side effects.
 */
interface ObserverInterface {
  api: { observe(): void; disconnect(): void };
  mutations: Observable<MutationRecord>;
}
const observeMutations = (node: Node, config: MutationObserverInit): ObserverInterface => {
  const subject = new Subject<MutationRecord>();
  const changes: MutationObserver = new MutationObserver((mutations: MutationRecord[]) => {
    for (const mutation of mutations) subject.next(mutation);
  });
  const api = {
    observe: () => {
      // eslint-disable-next-line no-restricted-globals
      changes.observe(node || document, config);
    },
    disconnect: () => {
      changes.disconnect();
    }
  };

  return { api, mutations: subject.asObservable() };
};

export const setupContext = createEffect(
  (
    actions$ = ngInject(Actions),
    settingsService = ngInject<SettingsService>(SettingsService),
    locationStorageService = ngInject<LocalStorageService>(LocalStorageService)
  ) =>
    actions$.pipe(
      ofType(initApp),
      map(() => {
        const getCarlSettingsFromLocalStorageOrSettings = (): { carlUrl: string; carlLabel: string } =>
          locationStorageService.get(OWCC_LOCAL_STORAGE_KEY.CARL_CONFIG, {
            carlUrl: settingsService.data.apis.carl.baseUrl,
            carlLabel: settingsService.data.apis.carl.label
          });

        const { carlUrl, carlLabel } = getCarlSettingsFromLocalStorageOrSettings();

        const getFormattingTagFromLocalStorageOrSettings = (): { formattingTag: string } =>
          locationStorageService.get(OWCC_LOCAL_STORAGE_KEY.FORMATTING_TAG, {
            formattingTag: settingsService.data.apis.backend.formattingTag
          });

        const { formattingTag } = getFormattingTagFromLocalStorageOrSettings();

        return runtimeVariablesUpdate({
          payload: {
            backendUrl: settingsService.data.apis.backend.baseUrl,
            formattingTag,
            carlUrl,
            carlLabel
          }
        });
      })
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * If query param `carlLabel` is defined in the URL the context gets updated accordingly
 */
export const updateContextWithCarlLabelFromUrlIfDefined = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(initApp),
      switchMap(() => appInject(Store).select(fromRouter.selectCarlLabelQueryParam)),
      filterTruthy(),
      map(carlLabel => runtimeVariablesUpdate({ payload: { carlLabel } }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * If query param `formattingTag` is defined in the URL the context gets updated accordingly
 */
export const updateContextWithFormattingTagFromUrlIfDefined = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(initApp),
      switchMap(() => appInject(Store).select(fromRouter.selectFormattingTagLabelQueryParam)),
      filterTruthy(),
      map(formattingTag => runtimeVariablesUpdate({ payload: { formattingTag } }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * Topic B2O / Direct Sales: when the user enters OWCC via online code page with a certain query parameter (`directSales`) the B2O modal opens instantly.
 * Purpose: Linkout from Wishlist with opened B2O modal.
 * If OnlineCode is invalid / outdated, user might get redirected to error page, in this case the modal won't show up.
 */
export const selectEnteringOnOnlineCodePageWithDirectSalesParameter = createEffect(
  (actions$ = ngInject(Actions)) => {
    const selectPrecondition = createSelector(
      fromCarl.selectHasLoadedCarlData,
      fromRouter.selectIsOnErrorPage,
      (hasLoadedCarlData, isError) => ({ hasLoadedCarlData, isError })
    );

    return actions$.pipe(
      ofType(initApp),
      switchMap(() => appInject(Store).select(selectPrecondition)),
      filter(({ hasLoadedCarlData }) => !!hasLoadedCarlData),
      switchMap(({ isError }) =>
        combineLatest([
          appInject(Store).pipe(b2oSelectors.selectEnteringOnOnlineCodePageWithDirectSalesParameter),
          of(isError)
        ])
      ),
      // debounce because: if online code is not valid anymore it takes a while until it's clear that its redirected to the error page
      // TODO: investigate if this hardcoded delay is really/still necessary
      debounceTime(DURATION_1_SEC),
      tap(([directSalesParameterValue, isError]) => {
        // we check for '!== false' on purpose as the parameter value is 1) ignored here and 2) can have no value (empty string).
        if (directSalesParameterValue !== false && !isError) {
          appInject(ModalService).open(LazyComponentId.BuildToOrder);
        }
      })
    );
  },
  { functional: true, dispatch: false }
);

/**
 * updates the document title
 * pattern: <selected vehicle name> - <navigation element> - <base title>
 * e.g. Mercedes-AMG GT 63 S E PERFORMANCE - Motorisierungen - Mercedes-Benz Car Configurator
 */
export const updateDocumentTitle = createEffect(
  (actions$ = ngInject(Actions), store = ngInject(Store)) =>
    actions$.pipe(
      ofType(initApp),
      switchMap(() =>
        combineLatest([
          store.select(selectSelectedVehicleName),
          store.select(fromNavigation.selectCurrentNavigationElementItem).pipe(
            filterTruthy(),
            mergeMap((item: NavigationItem) =>
              item.isTranslated ? appInject(Store).select(fromCarl.selectTranslation(item.name)) : of(item.name)
            )
          ),
          store.select(fromCarl.selectTranslation('baseTitle'))
        ])
      ),
      filter(Boolean),
      map(parts => parts.join(' - ')),
      tap((newTitle: string) => {
        appInject<Title>(Title).setTitle(newTitle);
      })
    ),
  { functional: true, dispatch: false }
);

/**
 * Evaluates whether in AEM preview or in OWCC runtime mode and return the corresponding actions to process the initialization.
 */
export const evaluateIfInAemPreviewOrRuntimeMode = createEffect(
  (actions$ = ngInject(Actions), windo = ngInject(WINDOW)) => {
    const isInAemPreviewMode$ = (win: Window): Observable<boolean> => {
      const { location } = win.top.document;
      return of(
        location.hostname.endsWith('.aem.oneweb.mercedes-benz.com') &&
          /^\/(sites|editor)\.html\//.test(location.pathname)
      );
    };

    const executionMode$ = (win: Window): Observable<ExecutionMode> => {
      const mode = win.owcc?.mode === ExecutionMode.Integrated ? ExecutionMode.Integrated : ExecutionMode.Standalone;
      return of(mode);
    };

    return actions$.pipe(
      ofType(initApp),
      switchMap(() => combineLatest([isInAemPreviewMode$(windo), executionMode$(windo)])),
      switchMap(([isInAemPreviewMode, executionMode]) => {
        if (isInAemPreviewMode) {
          return [setIsInAemPreviewMode({ isInAemPreviewMode: true })];
        }
        // TODO: Fix with MBCC-2538
        // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
        return [
          setIsInAemPreviewMode({ isInAemPreviewMode: false }),
          setBaseContextForRuntimeMode({ executionMode }),
          initAppForRuntimeMode({ executionMode })
        ];
      })
    );
  },
  {
    functional: true,
    dispatch: true
  }
);

export const onSetBaseContextForRuntimeMode = createEffect(
  (actions$ = ngInject(Actions), win = ngInject(WINDOW)) =>
    actions$.pipe(
      ofType(setBaseContextForRuntimeMode),
      map(({ executionMode }) => {
        const onewebLanguage = win.owcc?.language ?? null;
        const onewebCountry = win.owcc?.country ?? null;
        return udpateBaseContext({ executionMode, onewebLanguage, onewebCountry });
      })
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * When opening OWCC there might be an existing financial code in the _session storage_ - set by a previous page with an
 * integrated FCIS (financial calculator). If there is an existing financial code we take that over at startup
 * in order to pass it to further FCIS calculations to eventually / ideally show the same rate to the user as before
 * in the previous part of the journey.
 */
export const updateOnlineCodeInOwccStoreIfAvailableInSeamlessStore = createEffect(
  (actions$ = ngInject(Actions), sessionStorageService = ngInject<SessionStorageService>(SessionStorageService)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      filter(action => action.executionMode === ExecutionMode.Integrated),
      map(
        () =>
          sessionStorageService.get<{ financialCode: string }>(SEAMLESS_STORE_SESSION_STORAGE_KEY.FCIS_DATA)
            ?.financialCode
      ),
      first(),
      filterTruthy(),
      map(financialCode => runtimeVariablesUpdate({ payload: { financialCode } }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const observeProfileRoleUserChooser = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      filter(action => action.executionMode === ExecutionMode.Integrated),
      switchMap(() => seamlessStoreFns.observeProfileRoleUserChooser()),
      map(userChooser => runtimeVariablesUpdate({ payload: { userChooser } }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * In integrated mode a / any workbench version is provided by OWC which can be used by 3rd party components like FCIS.
 * In standalone mode we just provide our own WB version (e.g. 7), but 3rd party libs might depend on the 'default' WB version,
 * which is usually / in integrated mode provided by OWC. As it's n/a in standalone mode by default we dynamically load WB - in standalone mode.
 */
export const loadOwcWorkbenchVersionForThirdPartyComponents = createEffect(
  (
    actions$ = ngInject(Actions),
    win = ngInject(WINDOW),
    scriptLoadingService = ngInject<ScriptLoadingService>(ScriptLoadingService)
  ) => {
    const loadScript = (): void => {
      scriptLoadingService
        .load({
          scriptUrl: getScriptUrl('legacyWorkbench'),
          async: true,
          isModule: true
        })
        .catch(noop);
    };

    const loadStyles = (): void => {
      const doc = win.document;
      const styles = doc.createElement('link');
      styles.rel = 'stylesheet';
      styles.type = 'text/css';
      styles.href = URLS.styles.legacyWorkbench;
      doc.querySelector('head').append(styles);
    };

    return actions$.pipe(
      ofType(initAppForRuntimeMode),
      filter(action => action.executionMode === ExecutionMode.Standalone),
      tap(() => {
        loadScript();
      }),
      tap(() => {
        loadStyles();
      })
    );
  },
  { functional: true, dispatch: false }
);

// see https://pages.git.i.mercedes-benz.com/dh-io-owcc/documentation/tooling/b2x/
export const observeAuth = createEffect(
  (actions$ = ngInject(Actions)) => {
    const authObservableFromConsole = (): Observable<AuthenticationState> =>
      fromEventPattern<AuthenticationState>(handler => {
        appInject<ConsoleCommandService>(ConsoleCommandService).onUpdateAuth(state => {
          handler(JSON.parse(state));
        });
      });

    return actions$.pipe(
      ofType(initAppForRuntimeMode),
      // we consider standalone and integrated mode in order to handle real user logins and fake user logins via console
      filter(
        action => action.executionMode === ExecutionMode.Standalone || action.executionMode === ExecutionMode.Integrated
      ),
      switchMap(() => merge(seamlessStoreFns.observeAuth(), authObservableFromConsole())),
      map((authState: AuthenticationState) => updateAuthState({ authState }))
    );
  },
  {
    functional: true,
    dispatch: true
  }
);

export const fetchConfigAndTranslations = createEffect(
  (actions$ = ngInject(Actions), store = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      // we actually don't need the carl parameters, but when they got changed we need to re-fetch
      // config and translations (e.g. caused by change on doorway page)
      concatMap(() => store.pipe(selectConfigAndTranslationRelevantParams)),
      switchMap(() => combineLatest(appInject(CarlService).loadConfig(), appInject(CarlService).loadTranslations())),
      // TODO: Fix with MBCC-2538
      // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
      switchMap(([config, translations]) => [
        updateConfig({ config: flatten(config) }),
        updateTranslations({ translations: flatten(translations) })
      ])
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * Updates the store with the according MQ from workbench event.
 * Range between MQ1 to MQ6.
 */
export const updateMqInitiallyAndOnResize = createEffect(
  (actions$ = ngInject(Actions), win = ngInject(WINDOW)) => {
    /**
     * Converts WB mq (1-6) to OWCC mq enum (1-7)
     */
    const toMq = (mediaQuery: MediaQuery): Mq => mediaQuery as Mq;

    const resizeObservable: Observable<Mq> = merge(
      fromEvent(win, WB_RESIZE_EVENT_NAME).pipe(
        map((event: CustomEvent<MediaQueryChangeEvent>) => event.detail.current),
        map((mediaQuery: MediaQuery): Mq => toMq(mediaQuery))
      ),
      fromEvent(win, 'resize').pipe(
        // TODO: investigate if this hardcoded delay is really/still necessary
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        debounceTime(10),
        map(() => win.document.body.clientWidth),
        filter(width => width >= MQ6_MIN_WIDTH),
        map(width => (width >= MQ7_MIN_WIDTH ? Mq.Mq7 : Mq.Mq6))
      )
    );
    const initialObservable = (): Observable<Mq> => {
      const mediaQueryService = new MediaQueryService(win);
      const currentMediaQuery: MediaQuery = mediaQueryService.getCurrentMediaQuery();
      const isMq7 = win.document.body.clientWidth >= MQ7_MIN_WIDTH;
      return isMq7 ? of(Mq.Mq7) : of(toMq(currentMediaQuery));
    };

    return actions$.pipe(
      ofType(initAppForRuntimeMode),
      switchMap(() => merge(initialObservable(), resizeObservable)),
      distinctUntilChanged(),
      map((mq: Mq) => updateMediaQuery({ payload: mq }))
    );
  },
  {
    functional: true,
    dispatch: true
  }
);

export const initConsentSystem = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      tap(({ executionMode }: { executionMode: ExecutionMode }) => {
        appInject<ConsentSystemService>(ConsentSystemService).init(executionMode);
      })
    ),
  { functional: true, dispatch: false }
);

export const initAcknowledgementSystem = createEffect(
  (
    actions$ = ngInject(Actions),
    sessionStorageService = ngInject<SessionStorageService>(SessionStorageService),
    store$ = ngInject<Store>(Store)
  ) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode, routerNavigationEndAction),
      switchMap(() => store$.pipe(fromCarl.selectConfigOnce<AcknowledgementConfig>('acknowledgement'))),
      takeWhile(
        (acknowledgementConfig: AcknowledgementConfig) =>
          acknowledgementConfig?.isActive &&
          !sessionStorageService.get<boolean>(OWCC_SESSION_STORAGE_KEY.ACKNOWLEDGEMENT),
        true
      ),
      tap(() => {
        appInject<AcknowledgementService>(AcknowledgementService).init();
      })
    ),
  { functional: true, dispatch: false }
);

// there is a reason, but I cannot remember why we need this. once we know, lets document
export const setDocumentLangAttribute = createEffect(
  (actions$ = ngInject(Actions), win = ngInject(WINDOW), store = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      switchMap(() => store.select(selectCountryAndLanguageByIntegrationMode)),
      filterTruthy(obj => obj.country && obj.language),
      filterChanged(),
      tap(({ country, language }: { country: string; language: string }) => {
        const setLangAttribute = (languageParam: string, countryParam: string): void => {
          win.document.documentElement.setAttribute('lang', `${languageParam}-${countryParam}`);
        };

        setLangAttribute(language, country);
      })
    ),
  { functional: true, dispatch: false }
);

export const initDatadog = createEffect(
  (
    actions$ = ngInject(Actions),
    win = ngInject(WINDOW),
    datadogService = ngInject<DatadogService>(DatadogService),
    currentHostRefInject = ngInject(currentHostRef)
  ) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      tap(() => {
        datadogService.init();

        if (currentHostRefInject.isUnknown) {
          datadogService.warn(`Could not detect HostId; used '${HostId.Unknown}'`, {
            location: win.location
          });
        }
      })
    ),
  { functional: true, dispatch: false }
);

export const handleRegionPickerChange$ = createEffect(
  (actions$ = ngInject(Actions), store$ = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      filter(action => action.executionMode === ExecutionMode.Integrated),
      switchMap(() => store$.select(fromCarl.selectConfig<boolean>('financial.showRegionPicker', false))),
      filter(showRegionPicker => !!showRegionPicker),
      switchMap(() => seamlessStoreFns.observeRegionPicker()),
      // ℹ️ event gets fired twice from region picker with same data on close, that's why we make sure there was a change
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      // treat only on region picker close (don't react on open)
      filter(state => state.isOpen === false),
      // first the postal code needs to be updated before next actions can get triggered
      map((state: SeamlessStoreRegionPickerState) => userConfigUpdatePostalCode({ postalCode: state.region }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * dispatches actions if postal code / region got updated (emtpy if 'continue without prices') in order to reload necessary infos (vehicles) with price information
 */
export const onUpdatePostalCode$ = createEffect(
  (actions$ = ngInject(Actions), store$ = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(userConfigUpdatePostalCode),
      concatLatestFrom(() => store$.pipe(selectContextVehicleId)),
      // TODO: Fix with MBCC-2538
      // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
      switchMap(([, vehicleId]: [unknown, string]) => [
        startLoadVehicle({ vehicleId }),
        startLoadVehiclesAction(),
        // even for the same vehicle ID the vehicle summary can be different (depends if prices are shown or not), thus this invlidation
        invalidateVehicleSummaryDueToPostalCodeUpdate()
      ])
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * opens region picker integrated in OneWeb when OWCC demands it (clicks the button, which is available in certain countries (e.g. AU))
 */
export const handleRegionPickerOpening$ = createEffect(
  (actions$ = ngInject(Actions), store$ = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(openRegionPicker),
      concatLatestFrom(() => store$.select(selectCountry).pipe(map(country => country?.toLowerCase()))),
      tap(([, lowerCaseCountryCode]: [never, string]) => {
        seamlessStoreFns
          .importRegionPickerStore()
          .then(async storeConnection =>
            storeConnection?.getRegionPickerConnection(lowerCaseCountryCode)?.getRegisteredDispatchers()
          )
          .then(dispatchers => {
            dispatchers.setOpenState(true);
          })
          .catch(noop);
      })
    ),
  { functional: true, dispatch: false }
);

/**
 * Region picker might open automatically (depending on the market) if no region already selected.
 * Usually it activates scroll lock automatically, but OWCC reverts that somehow on OWCC load, that's why we handle it explicitely here.
 */
export const scrollLockByRegionPickerState$ = createEffect(
  (actions$ = ngInject(Actions), win = ngInject(WINDOW), viewPortScroller = ngInject(ViewportScroller)) =>
    actions$.pipe(
      ofType(initAppForRuntimeMode),
      switchMap(() => {
        const mutationObserverConfig: MutationObserverInit = {
          attributeOldValue: true,
          attributes: true,
          characterData: true,
          characterDataOldValue: true,
          childList: true,
          subtree: true
        };

        return combineLatest([
          of(observeMutations(win.document, mutationObserverConfig)),
          seamlessStoreFns.observeRegionPicker().pipe(
            map(state => state.isOpen),
            distinctUntilChanged()
          )
        ]);
      }),
      tap(([documentObserver, isRegionPickerOpen]) => {
        // only observe document if needed (meaning if region picker is open)
        if (isRegionPickerOpen) documentObserver.api.observe();
        else documentObserver.api.disconnect();
      }),
      switchMap(([documentObserver, isRegionPickerOpen]) =>
        combineLatest([
          of(isRegionPickerOpen),
          // we need to make sure scroll lock gets activated again after dom change (if region picker is open) to override other instructions
          documentObserver.mutations.pipe(
            filter(() => isRegionPickerOpen),
            startWith(undefined)
          )
        ])
      ),
      filter(([isRegionPickerOpen]) => !!isRegionPickerOpen),
      tap(() => {
        // maybe a user scrolled down before and reloads, thus we make sure to jump to top if region picker is open
        viewPortScroller.scrollToPosition([0, 0]);
      })
    ),
  { functional: true, dispatch: false }
);

// it looks like this action (set navigation groups) is done way to often (e.g. 3 times when you click on "your configuration")
export const triggerSetNavigationGroupForMoto$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(updateCurrentMotorization),
      map(({ vehicle: payload }) => startSetNavigationGroups({ componentCategories: payload.navigationCategories }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

/**
 * Set Navigation Groups
 * [start set Navigation Groups]
 *   -> [finish set Navigation Groups]
 */
export const setNavigationGroups$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(startSetNavigationGroups),
      map(action => action.componentCategories),
      filterTruthy(),
      switchMap(componentCategories =>
        combineLatest(
          of(componentCategories),
          appInject(Store).select(selectHasPreconfigs),
          appInject(Store).select(selectHasVehicleLengths)
        )
      ),
      map(([componentCategories, displayPreconfigPage, displayVehicleLengthPage]) =>
        getCustomNavigationGroups(componentCategories, !!displayPreconfigPage, !!displayVehicleLengthPage)
      ),
      map(navigationGroups => finishSetNavigationGroups({ navigationGroups }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const invalidateOnlineCodeOnMotorizationChange = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(finishUpdateFeasibleVehicleAction, finishUpdateVehicleAction),
      map(({ vehicle }) => updateOnlineCode({ vehicleId: vehicle.vehicleId }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const updateOnlineCodeAfterLoadVehicle = createEffect(
  (actions$ = ngInject(Actions), store$ = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(updateOnlineCode),
      concatLatestFrom(() => store$.select(selectOnlineCodeRelatedData)),
      map(([action, onlineCode]) => {
        const hasVehicleChanged = action.vehicleId !== onlineCode?.vehicleId;
        if (hasVehicleChanged) {
          invalidateOnlineCode();
          // important: Do not reset vcsid here. It needs to stay in the store, so the next OC call can provide the previous one.
        }
        return hasVehicleChanged;
      })
    ),
  { functional: true, dispatch: false }
);

/**
 * Does
 * - reload the configuration / translation on auth state change if required (e.g. if Sales Expert logs in)
 * - rewrite the URL accordingly to have the correct subprocess in the path
 */
export const handleAuthenticationStateChange = createEffect(
  (actions$ = ngInject(Actions), location = ngInject(Location), store = ngInject<Store>(Store)) =>
    actions$.pipe(
      ofType(updateAuthState),
      map(action => action.authState),
      map(authState => {
        const replaceInPath = (path: string, replacement: Subprocess): string => {
          const getReplaceableSubprocess = (p: string): Subprocess | undefined => {
            if (p.includes(`/${Subprocess.CCci}/`)) {
              return Subprocess.CCci;
            }
            if (p.includes(`/${Subprocess.CCri}/`)) {
              return Subprocess.CCri;
            }
            return undefined; // intentionally returning undefined, but _should_ never happen
          };

          const replaceableSubprocess: Subprocess = getReplaceableSubprocess(path);
          return replaceableSubprocess ? path.replace(`/${replaceableSubprocess}/`, `/${replacement}/`) : path;
        };

        /**
         * NOTE: if not in sales mode we need to either redirect to CCci or CCri, depending whether it's wholesale or a dealer.
         * With the new OneWeb integrated dealers pages we no longer can check for 'Standalone' or 'Integrated' but have to identify it
         * in another way (in this case outlet id).
         */
        const toNonSalesModeSubprocess = (isDealer: boolean): Subprocess =>
          isDealer ? Subprocess.CCri : Subprocess.CCci;

        const isLoggedInAsEmployee =
          authState && isLoggedIn(authState) && getIdentityProvider(authState) === IdentityProvider.BIAS;

        const hasOutletId =
          firstValueFromSync<string>(store.pipe(fromRouterAsSnapshot.selectOutletIdQueryParam)) !== undefined;
        const targetSubprocess = isLoggedInAsEmployee ? Subprocess.CCxi : toNonSalesModeSubprocess(hasOutletId);

        const getNewLocationState = (path: string, isEmployee: boolean, subprocess: Subprocess): string =>
          isEmployee ? replaceInPath(path, subprocess) : path.replace(`/${Subprocess.CCxi}/`, `/${subprocess}/`);

        location.replaceState(getNewLocationState(location.path(), isLoggedInAsEmployee, targetSubprocess));
        return runtimeVariablesUpdate({ payload: { subprocess: targetSubprocess } });
      })
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const fetchBackendDataVersion = createEffect(
  (actions$ = ngInject(Actions), statusService = ngInject(StatusService)) =>
    actions$.pipe(
      ofType(fetchBackendVersion),
      map(action => ({
        market: action.market,
        subProcess: action.subProcess
      })),
      switchMap(({ market, subProcess }) => statusService.version({ market, subProcess })),
      map((version: Version) => updateBackendVersion({ backendDataVersion: version.dataVersion }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const onFetchVehicleSummary = createEffect(
  (actions$ = ngInject(Actions), vehiclesService = ngInject(VehiclesService)) =>
    actions$.pipe(
      ofType(fetchVehicleSummary),
      concatLatestFrom(() => appInject(Store).select(selectApiContext)),
      switchMap(([{ vehicleId }, { market, subProcess, dataVersion }]) =>
        vehiclesService
          .vehicleSummary({ vehicleId, market, subProcess, dataVersion })
          .pipe(map(vehicleSummary => ({ vehicleSummary, vehicleId })))
      ),
      tap(result => {
        for (const hint of Object.keys(result?.vehicleSummary?.footnotes || [])) {
          appInject(FootnotesService).registerFootnote(hint, result?.vehicleSummary?.footnotes[hint]);
        }
      }),
      map(result => updateVehicleSummary({ result }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const observeScreenMirroringCredentials = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(initApp),
      switchMap(() =>
        fromEventPattern<ScreenMirroringCredentials>(handler => {
          appInject(ConsoleCommandService).onUpdateScreenMirroringCredentials(credentials => {
            handler(credentials);
          });
        })
      ),
      map((credentials: ScreenMirroringCredentials) => updateScreenMirroringCredentials({ credentials }))
    ),
  {
    functional: true,
    dispatch: true
  }
);

export const setOwccPublicStoreOnlineCode = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(updateOnlineCodeData),
      map(payload => payload.onlineCode),
      distinctUntilChanged(),
      map(onlineCode => {
        const data: PublicInformation = { onlineCode };
        return from(seamlessStoreFns.updateOWCCPublicStore(data));
      })
    ),
  {
    functional: true,
    dispatch: false
  }
);

export const selectSetOwccPublicStore = createEffect(
  (actions$ = ngInject(Actions), linkService = ngInject(LinkService), store = ngInject(Store)) => {
    const selectDataSelector = createSelector(
      selectCurrentMotorization,
      selectCommonVehicleImageUrl,
      selectOnlineCode,

      (motorizationVehicle, vehicleImageUrl, onlineCode) => ({
        motorizationVehicle,
        vehicleImageUrl,
        onlineCode
      })
    );

    return actions$.pipe(
      ofType(finishLoadSelectablesAction),
      filter(() => seamlessStoreFns.isSeamlessStoreAvailable()),
      switchMap(() => store.select(selectDataSelector)),
      distinctUntilChanged((prev, current) => {
        const sameName = prev.motorizationVehicle.name === current.motorizationVehicle.name;
        const sameVehicleId = prev.motorizationVehicle.vehicleId === current.motorizationVehicle.vehicleId;
        const sameImage = prev.vehicleImageUrl === current.vehicleImageUrl;
        const invalidImage = current.vehicleImageUrl && !current.vehicleImageUrl.includes('COSY'); // filtering out invalid images
        const onlineCodeSetThisTime = !!current.onlineCode && current.onlineCode !== prev.onlineCode; // when onlinecode is set and new, update
        return (sameName && sameVehicleId && sameImage && !onlineCodeSetThisTime) || invalidImage;
      }),
      mergeMap(({ motorizationVehicle, vehicleImageUrl, onlineCode }) => {
        // Future improvement: add baseImageUrl to the PublicInformation
        const data: PublicInformation = {
          vehicleId: motorizationVehicle?.vehicleId,
          vehicleName: motorizationVehicle?.name,
          stageImageUrl: vehicleImageUrl,
          onlineCode: onlineCode
        };

        const baseHref = appInject(APP_BASE_HREF);
        const window = appInject(WINDOW);
        const queryParams = window.location.search || '';

        return linkService.getConfigurationSummaryLink(motorizationVehicle?.vehicleId).pipe(
          map(summaryLink => {
            // summaryLink contains # if they are not replaced the url is not valid, decodeURIComponent isnt helping with that.
            // encodeURIComponent is affecting other parts of the url as well and leads to wrong urls too.
            const validSummaryLink = summaryLink.replaceAll(/#(?=.*\/summary)/g, '%23');
            const url = new URL(slashJoin(window.location.origin, baseHref, validSummaryLink) + queryParams);

            return { ...data, linkToSummaryWithVehicleId: url.toString() };
          })
        );
      }),
      tap(data => {
        seamlessStoreFns.updateOWCCPublicStore(data).catch(noop);
      })
    );
  },
  {
    functional: true,
    dispatch: false
  }
);
